import { Box, SpaceBetween, Button, Badge, Link } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { JourneyNodeData } from '../../utils/buildGraph';
import { ConfigurationObject } from '@features/configs/services/configs-api';
import { formatDate, resolveConfigName, formatOnOff } from '@features/configs/components/detail/format-utils';

interface NodeInspectPanelProps {
  nodeData: JourneyNodeData | null;
  allConfigs: ConfigurationObject[];
  onClose: () => void;
  onNodeNavigate: (configId: string) => void;
}

/**
 * Slide-in side panel for inspecting a node in the journey diagram.
 * Shows type-specific key fields, last modified, and action buttons.
 * BR-14: REQ-5
 */
export function NodeInspectPanel({ nodeData, allConfigs, onClose, onNodeNavigate }: NodeInspectPanelProps) {
  const navigate = useNavigate();

  if (!nodeData) return null;

  const { config, role } = nodeData;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 300,
        height: '100%',
        background: '#FFFFFF',
        borderLeft: '1px solid #E2E8F0',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
        padding: 20,
        overflowY: 'auto',
        zIndex: 10,
      }}
    >
      <SpaceBetween size="m">
        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Badge color={role === 'entry' ? 'blue' : role === 'routing' ? 'blue' : role === 'resolution' ? 'green' : 'grey'}>
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </Badge>
          <Button variant="icon" iconName="close" onClick={onClose} ariaLabel="Close panel" />
        </div>

        {/* Config name and ID */}
        <div>
          <Box variant="h3">{config.configName}</Box>
          <Box variant="small" color="text-body-secondary" fontWeight="normal">
            <code style={{ fontSize: 11, color: '#94A3B8' }}>{config.configId}</code>
          </Box>
        </div>

        {/* Type-specific key fields */}
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          {renderKeyFields(config, allConfigs, onNodeNavigate)}
        </div>

        {/* Metadata */}
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          <SpaceBetween size="xs">
            <Box variant="small"><strong>Last Modified:</strong> {formatDate(config.lastModified)}</Box>
            <Box variant="small"><strong>Modified By:</strong> {config.lastModifiedBy}</Box>
          </SpaceBetween>
        </div>

        {/* Actions */}
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate(`/configs/${config.configId}/edit`)}>Edit</Button>
            <Button onClick={() => navigate('/configs/new', { state: { duplicate: true, source: config } })}>Duplicate</Button>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </div>
  );
}

/**
 * Renders type-specific key fields for the side panel.
 */
function renderKeyFields(
  config: ConfigurationObject,
  allConfigs: ConfigurationObject[],
  onNodeNavigate: (configId: string) => void
) {
  const payload = config.payload ?? {};

  switch (config.configType) {
    case 'menus': {
      const prompt = (payload.menuPrompt as string) ?? '—';
      const options: { num: number; name: string; id: string }[] = [];
      for (let i = 1; i <= 8; i++) {
        const intentId = payload[`option${i}Intent`] as string | undefined;
        if (intentId) {
          options.push({ num: i, name: resolveConfigName(intentId, allConfigs), id: intentId });
        }
      }
      return (
        <SpaceBetween size="xs">
          <Box variant="small"><strong>Menu Prompt:</strong> {prompt}</Box>
          {options.map((opt) => (
            <Box key={opt.num} variant="small">
              Option {opt.num} →{' '}
              <Link onFollow={(e) => { e.preventDefault(); onNodeNavigate(opt.id); }}>{opt.name}</Link>
            </Box>
          ))}
        </SpaceBetween>
      );
    }

    case 'intents': {
      const destType = (payload.destinationType as string) ?? '—';
      const destValue = (payload.destinationValue as string) ?? '';
      const condition = (payload.conditionalExpression as string) ?? '';
      return (
        <SpaceBetween size="xs">
          <Box variant="small"><strong>Routes to:</strong> {destType} → {resolveConfigName(destValue, allConfigs)}</Box>
          <Box variant="small"><strong>Condition:</strong> {condition || 'Always routes'}</Box>
        </SpaceBetween>
      );
    }

    case 'queues': {
      const bhId = payload.businessHoursSchedule as string | undefined;
      const emergency = formatOnOff(payload.emergencyEnabled);
      return (
        <SpaceBetween size="xs">
          {bhId && (
            <Box variant="small">
              <strong>Business Hours:</strong>{' '}
              <Link onFollow={(e) => { e.preventDefault(); onNodeNavigate(bhId); }}>{resolveConfigName(bhId, allConfigs)}</Link>
            </Box>
          )}
          <Box variant="small"><strong>Emergency Mode:</strong> <span style={{ color: emergency.colour === 'green' ? '#037F0C' : '#D13212' }}>{emergency.text}</span></Box>
        </SpaceBetween>
      );
    }

    case 'dnis': {
      const welcome = (payload.welcomeMessage as string) ?? '';
      const destType = (payload.destinationType as string) ?? '—';
      const destValue = (payload.destinationValue as string) ?? '';
      const bhId = payload.businessHoursSchedule as string | undefined;
      return (
        <SpaceBetween size="xs">
          <Box variant="small"><strong>Welcome:</strong> {welcome.length > 80 ? welcome.slice(0, 80) + '…' : welcome || '—'}</Box>
          <Box variant="small"><strong>Destination:</strong> {destType} → {resolveConfigName(destValue, allConfigs)}</Box>
          {bhId && (
            <Box variant="small">
              <strong>Business Hours:</strong>{' '}
              <Link onFollow={(e) => { e.preventDefault(); onNodeNavigate(bhId); }}>{resolveConfigName(bhId, allConfigs)}</Link>
            </Box>
          )}
        </SpaceBetween>
      );
    }

    case 'business-hours': {
      const tz = (payload.timeZone as string) ?? '—';
      return (
        <SpaceBetween size="xs">
          <Box variant="small"><strong>Timezone:</strong> {tz}</Box>
        </SpaceBetween>
      );
    }

    default:
      return <Box variant="small" color="text-status-inactive">No additional details</Box>;
  }
}
